// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { useAuthSession } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import {
    MY_CLIENT_LIST_VIEW_CLASS_NAME,
    MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME
} from "../../../../../core/constants/className";
import {
    T_MY_CLIENT_LIST_VIEW_TITLE,
    T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_COMPANY,
    T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_PERSON,
    T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_ANY_CLIENT_LABEL,
    T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_OTHER
} from "../../../../../core/constants/translation";
import { useMyClientList } from "../../../../hooks/useMyClientList";
import { useEffect } from "react";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { useNavigateToClient } from "../../../../hooks/useNavigateToClient";
import { MySelectClientArticle } from "../selectClient/MySelectClientArticle";
import { MY_LOGIN_ROUTE } from "../../../../../core/constants/route";
import { useNavigate } from "react-router-dom";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import "./MyClientListView.scss";

export interface MyClientListViewProps {
    readonly className ?: string;
    readonly t?: TranslationFunction;
}

export function MyClientListView (props: MyClientListViewProps) {

    const t: TranslationFunction = props?.t ?? ((key: string): string => key);
    const className = props?.className;
    const navigate = useNavigate();
    const session = useAuthSession();
    const email = session?.email;
    const sms = session?.sms;
    const isLoggedIn : boolean = session?.isLoggedIn ?? false;
    const [list/*, refreshList*/] = useMyClientList(session?.token);
    const translationParams = {
        EMAIL: email ?? '',
        SMS: sms ?? '',
    };
    const selectClient = useNavigateToClient(navigate);

    // Navigate to login if not logged in
    useEffect(
        () => {
            if ( !isLoggedIn ) {
                navigate(MY_LOGIN_ROUTE);
            }
        },
        [
            isLoggedIn,
            navigate
        ]
    );

    if ( !isLoggedIn || !list ) {
        return (
            <Loader
                className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
            />
        );
    }

    if (!session) throw new TypeError('No session');

    return (
        <div className={
            MY_CLIENT_LIST_VIEW_CLASS_NAME
            + (className? ` ${className}` : '')
        }>
            <MySelectClientArticle
                t={t}
                className={`${MY_ORDER_AUTHORIZED_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
                title={t(T_MY_CLIENT_LIST_VIEW_TITLE, translationParams)}
                session={session}
                client={undefined}
                list={list}
                selectClient={selectClient}
                translationParams={translationParams}
                selectAnyClientLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_ANY_CLIENT_LABEL, translationParams)}
                newCompanyButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_COMPANY, translationParams)}
                newPersonButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_NEW_PERSON, translationParams)}
                reLoginButtonLabel={t(T_MY_ORDER_VIEW_SELECT_CLIENT_SELECT_OTHER, translationParams)}
            />
        </div>
    );

}
